import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { fbt } from 'fbtee'
import { pageTitle, useDocumentTitle } from '@/hooks/useDocumentTitle'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  type AuthError,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { signInWithGoogle, signInWithApple } from '@/lib/auth'
import { track } from '@/lib/analytics'
import { takeSignupSource } from '@/lib/signupSource'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BeaconLogo } from '@/components/BeaconLogo'
import { formerlyBeaconLabel, passwordResetSentMessage } from '@/lib/commonStrings'
import { isRenameBridgeActive } from '@/lib/rename'
import {
  emailErrorMessage,
  isUserCancel,
  providerErrorMessage,
  reportAuthError,
  type LoadingKey,
  type ProviderMethod,
} from './authErrors'
import { ProviderButtons } from './ProviderButtons'

type Mode = 'signin' | 'signup'

export default function LoginPage() {
  useDocumentTitle(pageTitle(fbt('Sign in', 'Browser tab title for the sign-in page')))
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState<LoadingKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [providerError, setProviderError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  function clearMessages() {
    setError(null)
    setProviderError(null)
    setInfo(null)
  }

  async function handleProviderSignIn(method: ProviderMethod) {
    setLoading(method)
    clearMessages()
    try {
      const cred = await (method === 'google' ? signInWithGoogle() : signInWithApple())
      const isFirstSignIn = cred.user.metadata.creationTime === cred.user.metadata.lastSignInTime
      if (isFirstSignIn) track('sign_up', { method, entry_point: takeSignupSource() })
      track('login', { method })
    } catch (err) {
      reportAuthError(`${method} sign-in`, err)
      if (!isUserCancel(err)) setProviderError(providerErrorMessage(method))
    } finally {
      setLoading(null)
    }
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmedEmail = email.trim()
    setLoading('email')
    clearMessages()
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, trimmedEmail, password)
        track('sign_up', { method: 'password', entry_point: takeSignupSource() })
        track('login', { method: 'password' })
      } else {
        await signInWithEmailAndPassword(auth, trimmedEmail, password)
        track('login', { method: 'password' })
      }
    } catch (err) {
      reportAuthError(mode === 'signup' ? 'email sign-up' : 'email sign-in', err)
      setError(emailErrorMessage((err as AuthError)?.code))
    } finally {
      setLoading(null)
    }
  }

  async function handleResetPassword() {
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setInfo(null)
      setProviderError(null)
      setError(
        fbt(
          'Enter your email above, then tap Forgot password again.',
          'Inline hint shown when user clicks Forgot password without filling the email field',
        ),
      )
      return
    }
    setLoading('reset')
    clearMessages()
    try {
      await sendPasswordResetEmail(auth, trimmedEmail)
      setInfo(passwordResetSentMessage())
    } catch (err) {
      reportAuthError('password reset', err)
      setError(emailErrorMessage((err as AuthError)?.code))
    } finally {
      setLoading(null)
    }
  }

  function switchMode(next: Mode) {
    setMode(next)
    clearMessages()
  }

  const busy = loading !== null

  return (
    <div className="min-h-full bg-background flex flex-col items-center justify-center p-6">
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
        <div className="h-[600px] w-[600px] rounded-full bg-primary/15 blur-[120px]" />
      </div>

      <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-4">
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-primary/30 animate-beacon-ping motion-reduce:hidden"
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-primary/30 animate-beacon-ping motion-reduce:hidden"
              style={{ animationDelay: '1.5s' }}
            />
            <BeaconLogo className="relative h-20 w-20" />
          </div>
          <h1 className="text-5xl font-bold tracking-tight text-foreground">Faro</h1>
          {isRenameBridgeActive() && (
            <p className="mt-1 text-sm text-muted-foreground/70">{formerlyBeaconLabel()}</p>
          )}
          <p className="mt-3 text-muted-foreground">
            <fbt desc="App tagline displayed below the logo on the login page">
              Coordinate festival plans with your friends.
            </fbt>
          </p>
        </div>

        <div className="w-full rounded-2xl border border-border bg-foreground/5 backdrop-blur-sm p-6 flex flex-col gap-4">
          <ProviderButtons
            loading={loading}
            busy={busy}
            error={providerError}
            onSignIn={handleProviderSignIn}
          />

          <div className="flex items-center gap-3 text-xs uppercase text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            <span>
              <fbt desc="Divider word between social sign-in buttons and the email/password form">
                or
              </fbt>
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">
                <fbt desc="Form label for the email input">Email</fbt>
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={busy}
                required
                className="h-10"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  <fbt desc="Form label for the password input">Password</fbt>
                </Label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={busy}
                    className="text-xs text-muted-foreground hover:text-foreground cursor-pointer disabled:opacity-50"
                  >
                    {loading === 'reset'
                      ? fbt('Sending…', 'Inline status while a password reset email is being sent')
                      : fbt('Forgot password?', 'Link that triggers a password reset email')}
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={busy}
                required
                minLength={mode === 'signup' ? 6 : undefined}
                className="h-10"
              />
            </div>
            <Button type="submit" disabled={busy} className="h-11 mt-1 font-medium">
              {loading === 'email'
                ? fbt(
                    'Working…',
                    'Button label while email/password sign-in or sign-up is processing',
                  )
                : mode === 'signup'
                  ? fbt('Create account', 'Submit button label for the email/password sign-up form')
                  : fbt('Sign in', 'Submit button label for the email/password sign-in form')}
            </Button>
          </form>

          {error && <p className="text-center text-sm text-destructive">{error}</p>}
          {info && <p className="text-center text-sm text-muted-foreground">{info}</p>}

          <p className="text-center text-xs text-muted-foreground leading-relaxed">
            <fbt desc="Legal consent line on the login page, with links to Terms and Privacy Policy">
              By continuing, you agree to our{' '}
              <fbt:param name="termsLink">
                <Link to="/terms" className="underline hover:text-foreground">
                  {fbt('Terms of Service', 'Inline link text for Terms of Service on the login page')}
                </Link>
              </fbt:param>{' '}
              and{' '}
              <fbt:param name="privacyLink">
                <Link to="/privacy" className="underline hover:text-foreground">
                  {fbt('Privacy Policy', 'Inline link text for Privacy Policy on the login page')}
                </Link>
              </fbt:param>.
            </fbt>
          </p>

          <p className="text-center text-sm text-muted-foreground">
            {mode === 'signup' ? (
              <>
                <fbt desc="Prompt next to the link that toggles from sign-up back to sign-in">
                  Already have an account?
                </fbt>{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="text-foreground hover:underline cursor-pointer"
                >
                  {fbt('Sign in', 'Link to switch from the sign-up form back to the sign-in form')}
                </button>
              </>
            ) : (
              <>
                <fbt desc="Prompt next to the link that toggles from sign-in to sign-up">
                  New to Faro?
                </fbt>{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-foreground hover:underline cursor-pointer"
                >
                  {fbt(
                    'Create an account',
                    'Link to switch from the sign-in form to the sign-up form',
                  )}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}
